import React from 'react';

enum HeadingLevel {
  H1 = 1,
  H2 = 2,
  H3 = 3,
  H4 = 4,
  H5 = 5,
  H6 = 6,
}

enum HeadingVariant {
  HERO = 'hero',
  PAGE_TITLE = 'page-title',
  SECTION_TITLE = 'section-title',
  CARD_TITLE = 'card-title',
  SUBTITLE = 'subtitle',
}

enum HeadingWeight {
  LIGHT = 'light',
  NORMAL = 'normal',
  MEDIUM = 'medium',
  SEMIBOLD = 'semibold',
  BOLD = 'bold',
  EXTRABOLD = 'extrabold',
}

enum HeadingColor {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  MUTED = 'muted',
  WHITE = 'white',
  INHERIT = 'inherit',
}

enum MarginBottom {
  NONE = 'none',
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
}

interface HeadingProps {
  children: React.ReactNode;
  level?: HeadingLevel;
  variant?: HeadingVariant;
  weight?: HeadingWeight;
  color?: HeadingColor;
  className?: string;
  marginBottom?: MarginBottom;
}

const variantClasses = {
  hero: 'text-4xl md:text-5xl leading-tight',
  'page-title': 'text-3xl md:text-4xl leading-tight',
  'section-title': 'text-2xl md:text-3xl leading-tight',
  'card-title': 'text-xl md:text-2xl leading-tight',
  subtitle: 'text-lg md:text-xl leading-relaxed',
};

const weightClasses = {
  light: 'font-light',
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  extrabold: 'font-extrabold',
};

const colorClasses = {
  primary: 'text-gray-900',
  secondary: 'text-gray-700',
  muted: 'text-gray-600',
  white: 'text-white',
  inherit: 'text-inherit',
};

const marginBottomClasses = {
  none: 'mb-0',
  xs: 'mb-1',
  sm: 'mb-2',
  md: 'mb-2',
  lg: 'mb-2',
  xl: 'mb-2',
};

export {HeadingLevel, HeadingVariant, HeadingWeight, HeadingColor, MarginBottom};

export const Heading: React.FC<HeadingProps> = ({
  children,
  level = HeadingLevel.H1,
  variant = HeadingVariant.HERO,
  weight = HeadingWeight.BOLD,
  color = HeadingColor.PRIMARY,
  className = '',
  marginBottom = MarginBottom.LG,
}) => {
  const Tag = `h${level}` as keyof JSX.IntrinsicElements;

  const classes = [variantClasses[variant], weightClasses[weight], colorClasses[color], marginBottomClasses[marginBottom], className]
    .filter(Boolean)
    .join(' ');

  return <Tag className={classes}>{children}</Tag>;
};
